import { useEffect, useState, useCallback } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/StatusBadge";
import { Bse2Badge } from "@/components/Bse2Badge";
import { formatCurrency, formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Download, ExternalLink, RefreshCw, Send } from "lucide-react";
import { CheckField, ChoiceCards, FormField, SchemePicker, UatResult } from "@/components/bse/BseUatForm";
import { useNavigate } from "react-router-dom";

const FLOW_META = {
  purchase: { title: "Buy / Purchase", filter: "PURCHASE", path: "/admin/orders" },
  redemption: { title: "Sell / Redeem", filter: "REDEMPTION", path: "/admin/redemptions" },
  switch: { title: "Switch", filter: "SWITCH", path: "/admin/switches" },
};

export default function OrdersMonitor({ initialKind = "purchase" }) {
  const navigate = useNavigate();
  const [orderType, setOrderType] = useState(FLOW_META[initialKind].filter);
  const [status, setStatus] = useState("");
  const [clientCode, setClientCode] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [creationResult, setCreationResult] = useState(null);
  const [form, setForm] = useState({
    kind: initialKind, client_code: "", scheme_code: "", destination_scheme_code: "",
    value_mode: "amount", amount: "", units: "", folio_no: "", bank_account_no: "", demat: false,
    source_amc_code: "", min_redeem_flag: false,
  });
  const setFormField = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  useEffect(() => {
    setOrderType(FLOW_META[initialKind].filter);
    setForm((previous) => ({ ...previous, kind: initialKind, scheme_code: "", destination_scheme_code: "",
      source_amc_code: "", value_mode: initialKind === "purchase" ? "amount" : previous.value_mode }));
    setCreationResult(null);
  }, [initialKind]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 50 };
      if (orderType && orderType !== "SWITCH") params.order_type = orderType;
      if (status) params.status = status;
      if (clientCode.trim()) params.client_code = clientCode.trim();
      if (orderType === "SWITCH") {
        const { data: switches } = await adminApi.get("/admin/switches");
        const items = switches.filter((item) => (!clientCode.trim() || item.client_code === clientCode.trim()) && (!status || item.status === status));
        setData({ items, total: items.length });
      } else if (!orderType) {
        const [{ data: orders }, { data: switches }] = await Promise.all([
          adminApi.get("/admin/orders", { params }), adminApi.get("/admin/switches"),
        ]);
        const matchingSwitches = switches.filter((item) => (!clientCode.trim() || item.client_code === clientCode.trim()) && (!status || item.status === status));
        const items = [...orders.items, ...matchingSwitches]
          .sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))).slice(0, 50);
        setData({ items, total: orders.total + matchingSwitches.length });
      } else {
        const { data } = await adminApi.get("/admin/orders", { params });
        setData(data);
      }
    } finally { setLoading(false); }
  }, [orderType, status, clientCode]);

  useEffect(() => { load(); }, [load]);


  const exportCsv = async () => {
    try {
      const response = await adminApi.get("/admin/export/orders.csv", { responseType: "blob" });
      const blob = response.data;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "orders_export.csv"; a.click();
        URL.revokeObjectURL(url);
        toast.success("Order CSV downloaded");
    } catch (error) { toast.error(getApiErrorMessage(error)); }
  };

  const refreshBse = async (orderNumber) => {
    try {
      const { data: result } = await adminApi.post(`/admin/orders/${orderNumber}/bse-refresh`);
      toast.success(result.message || `BSE status: ${result.live_status || "refreshed"}`);
      load();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
  };

  const submitOrder = async () => {
    if (!form.client_code.trim() || !form.scheme_code) return toast.error("Enter the UCC and choose a scheme");
    if (form.kind === "switch" && !form.destination_scheme_code) return toast.error("Choose the destination scheme");
    if (form.kind !== "purchase" && !form.folio_no.trim()) return toast.error("Enter the source folio number");
    if (form.value_mode !== "all" && !Number(form.value_mode === "amount" ? form.amount : form.units)) {
      return toast.error(`Enter the ${form.value_mode}`);
    }
    const description = form.kind === "purchase" ? "purchase" : form.kind === "redemption" ? "redemption" : "switch";
    if (!window.confirm(`Submit this ${description} to BSE UAT for ${form.client_code.toUpperCase()}?`)) return;
    const common = {
      client_code: form.client_code.trim().toUpperCase(), folio_no: form.folio_no || null,
      demat: form.demat,
    };
    let payload;
    if (form.kind === "purchase") {
      payload = { ...common, scheme_code: form.scheme_code, amount: Number(form.amount),
        bank_account_no: form.bank_account_no || null };
    } else if (form.kind === "redemption") {
      payload = { ...common, scheme_code: form.scheme_code, bank_account_no: form.bank_account_no || null,
        amount: form.value_mode === "amount" ? Number(form.amount) : null,
        qty: form.value_mode === "units" ? Number(form.units) : null, all_redeem: form.value_mode === "all",
        min_redeem_flag: form.min_redeem_flag };
    } else {
      payload = { ...common, from_scheme_code: form.scheme_code, to_scheme_code: form.destination_scheme_code,
        amount: form.value_mode === "amount" ? Number(form.amount) : null,
        units: form.value_mode === "units" ? Number(form.units) : null, all_units: form.value_mode === "all" };
    }
    setCreating(true); setCreationResult(null);
    try {
      const { data: result } = await adminApi.post(`/admin/bse2/transactions/orders/${form.kind}`, payload);
      setCreationResult(result);
      const bse = result.bse2 || {};
      bse.status === "SUBMITTED" ? toast.success("Order submitted to BSE UAT") : toast.error(bse.message || "BSE did not accept the order");
      await load();
    } catch (error) { toast.error(getApiErrorMessage(error)); }
    finally { setCreating(false); }
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div><h1 className="font-heading text-3xl text-[var(--brand)]">{FLOW_META[initialKind].title}</h1><p className="mt-1 text-sm text-[var(--ink-muted)]">Create and inspect purchase, redemption and switch orders in BSE UAT.</p></div>
        <Button variant="outline" className="rounded-full" onClick={exportCsv} data-testid="admin-orders-export-button"><Download className="mr-2 h-4 w-4" />Export CSV</Button>
      </div>

      <Card className="mb-6 border-[var(--glass-border)]">
        <CardHeader><CardTitle className="text-base">Create an order in BSE UAT</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <ChoiceCards value={form.kind} onChange={(value) => navigate(FLOW_META[value].path)}
            options={[
              { value: "purchase", label: "Purchase", help: "Invest a rupee amount into one scheme." },
              { value: "redemption", label: "Redemption", help: "Withdraw by amount, units or all units." },
              { value: "switch", label: "Switch", help: "Move holdings from one scheme to another." },
            ]} />
          <div className="grid gap-4 md:grid-cols-2">
            <FormField label="Investor UCC" value={form.client_code} onChange={(event) => setFormField("client_code", event.target.value.toUpperCase())}
              placeholder="Enter the UCC created during onboarding" help="Only this BSE identifier is needed; the client directory remains hidden." />
            <SchemePicker label={form.kind === "switch" ? "Source scheme" : "Scheme"} value={form.scheme_code}
              onChange={(value) => setFormField("scheme_code", value)}
              onSelect={(scheme) => setFormField("source_amc_code", scheme?.amc_code || "")}
              flags={form.kind === "switch" ? { switch_flag: true } : form.kind === "redemption" ? { redemption_flag: true } : { purchase_flag: true }} />
            {form.kind === "switch" && <SchemePicker label="Destination scheme" value={form.destination_scheme_code}
              onChange={(value) => setFormField("destination_scheme_code", value)}
              flags={{ switch_flag: true, amc_code: form.source_amc_code || undefined }} />}
            {form.kind !== "purchase" && <ChoiceCards label="How much?" value={form.value_mode} onChange={(value) => setFormField("value_mode", value)}
              options={[{ value: "amount", label: "Rupee amount" }, { value: "units", label: "Units" }, ...(!form.demat ? [{ value: "all", label: "All units" }] : [])]} />}
            {(form.kind === "purchase" || form.value_mode === "amount") && <FormField label="Amount (₹)" type="number" min="0" step="0.01"
              value={form.amount} onChange={(event) => setFormField("amount", event.target.value)} placeholder="5000" />}
            {form.kind !== "purchase" && form.value_mode === "units" && <FormField label="Units" type="number" min="0" step="0.001"
              value={form.units} onChange={(event) => setFormField("units", event.target.value)} placeholder="10.000" />}
            <FormField label={form.kind === "purchase" ? "Existing folio number (optional)" : "Source folio number"} value={form.folio_no}
              onChange={(event) => setFormField("folio_no", event.target.value)}
              placeholder={form.kind === "purchase" ? "Leave blank for a fresh purchase" : "Required for redemption and switch"}
              help={form.kind === "purchase" ? "Enter a folio for an additional purchase; leave it blank for the first purchase." : undefined} />
            {form.kind !== "switch" && <FormField label="Bank account number (optional)" value={form.bank_account_no}
              onChange={(event) => setFormField("bank_account_no", event.target.value)} />}
          </div>
          {form.kind === "redemption" && <CheckField label="Allow minimum-redemption residual handling" checked={form.min_redeem_flag} onChange={(value) => setFormField("min_redeem_flag", value)} help="Use BSE's min_redeem_flag when redeeming would otherwise leave a balance below the scheme minimum." />}
          <CheckField label="Place in demat mode" checked={form.demat} onChange={(value) => setForm((previous) => ({ ...previous, demat: value, value_mode: value && previous.value_mode === "all" ? "units" : previous.value_mode }))}
            help="Use only when the selected UCC has a registered NSDL/CDSL account." />
          <UatResult result={creationResult} />
          <Button onClick={submitOrder} disabled={creating} className="rounded-full bg-[var(--cta)] hover:bg-[#087a56]">
            <Send className="mr-2 h-4 w-4" />{creating ? "Submitting to BSE…" : `Submit ${form.kind} to BSE UAT`}
          </Button>
        </CardContent>
      </Card>

      <Card className="mb-4 border-[var(--glass-border)]">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row">
          <Select value={orderType || "__all"} onValueChange={(value) => setOrderType(value === "__all" ? "" : value)}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-orders-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All types</SelectItem><SelectItem value="PURCHASE">Purchase</SelectItem><SelectItem value="REDEMPTION">Redemption</SelectItem><SelectItem value="SWITCH">Switch</SelectItem></SelectContent>
          </Select>
          <Select value={status || "__all"} onValueChange={(value) => setStatus(value === "__all" ? "" : value)}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="admin-orders-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All statuses</SelectItem>
              {["RECEIVED", "PAYMENT_PENDING", "PAYMENT_CONFIRMED", "SENT_TO_RTA", "ALLOTTED", "REJECTED"].map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input className="w-full sm:w-[220px]" value={clientCode} onChange={(e) => setClientCode(e.target.value.toUpperCase())} placeholder="Filter by client code" />
        </CardContent>
      </Card>

      <Card className="border-[var(--glass-border)]">
        <CardContent className="p-0" data-testid="admin-orders-table">
          {loading ? <div className="p-6 text-sm text-[var(--ink-muted)]">Loading...</div> :
            data.items.length === 0 ? <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid="admin-orders-empty">No orders found.</div> :
            <div className="divide-y divide-[var(--glass-border)]">
              {data.items.map((o) => (
                <div key={o.order_number || o.order_id} className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`admin-order-row-${o.order_number || o.order_id}`}>
                  <div>
                    <div className="font-medium">{o.scheme_name || `${o.from_scheme_name || o.from_scheme_cd} → ${o.to_scheme_name || o.to_scheme_cd}`}</div>
                    <div className="text-xs text-[var(--ink-muted)]">Client {o.client_code} &middot; Order #{o.order_number || o.order_id} &middot; {formatDateTime(o.created_at)}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end sm:gap-3">
                    <div className="font-medium">{o.amount || o.switch_amount ? formatCurrency(o.amount || o.switch_amount) : `${o.qty || o.switch_units || "—"} units`}</div>
                    <StatusBadge status={o.status} />
                    <Bse2Badge bse2={o.bse2} />
                    {o.bse2?.auth_link && <Button asChild size="sm" className="rounded-full bg-[var(--cta)]"><a href={o.bse2.auth_link} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" /> Investor auth</a></Button>}
                    {o.order_number && o.bse2?.bse_order_id && <Button size="sm" variant="outline" className="rounded-full" onClick={() => refreshBse(o.order_number)}><RefreshCw className="mr-1 h-3.5 w-3.5" /> BSE status</Button>}
                  </div>
                </div>
              ))}
            </div>}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
